import {
  type PatientCancellationReason,
  PatientCancellationReasonSchema,
  type PublicAppointmentSession,
  type PublicAvailabilitySlot,
  type PublicManageAvailabilityResponse,
} from "@emr/contracts";
import { useCallback, useEffect, useRef, useState } from "react";
import { AddToCalendar } from "../AddToCalendar";
import { Icon } from "../Icon";
import { formatDateTime, formatDateTimeRange, telephoneHref } from "../lib/appointmentTime";
import { RequiredMarker } from "../RequiredMarker";
import {
  initialTimesDraft,
  type ManageTimesSearch,
  RescheduleTimes,
  type TimesDraft,
} from "./RescheduleTimes";

/**
 * Where a manage request leaves the page: the appointment's details, the list of times (the time
 * the patient picked was just taken), or the same step (the refusal is shown and they may retry).
 */
export type ManageOutcome = "details" | "times" | "stay";

/** What the manage page asks of the clinic; PublicPatientLane holds the link and sends them. */
export interface ManageActions {
  confirm(): Promise<ManageOutcome>;
  cancel(reason: PatientCancellationReason | undefined): Promise<ManageOutcome>;
  loadTimes(
    search: ManageTimesSearch,
    signal: AbortSignal,
  ): Promise<PublicManageAvailabilityResponse>;
  reschedule(slot: PublicAvailabilitySlot, physician: string | null): Promise<ManageOutcome>;
  /** Clears the last refusal when the patient moves to another step. */
  clearError(): void;
}

/** The five reasons, as the patient reads them (the clinic reads its own labels). */
export const cancellationReasonLabels: Record<PatientCancellationReason, string> = {
  schedule_conflict: "The time no longer works for me",
  feeling_better: "I'm feeling better",
  booked_elsewhere: "I'm getting care somewhere else",
  transportation: "I can't get to the clinic",
  other: "Another reason",
};

type Step = "details" | "cancel-reason" | "cancel-confirm" | "times" | "reschedule-confirm";

/** Ends a sentence once: a time such as "9:00 a.m." already ends with a period. */
function sentence(text: string): string {
  return text.endsWith(".") ? text : `${text}.`;
}

function statusLabel(status: PublicAppointmentSession["status"]): string {
  if (status === "booked") return "Booked";
  if (status === "confirmed") return "Confirmed";
  const words = status.replaceAll("_", " ");
  return `${words.charAt(0).toUpperCase()}${words.slice(1)}`;
}

/** "Call the clinic": the site phone as a link when it is dialable, or where to find it. */
export function CallTheClinic({ phone }: { readonly phone: string | undefined }) {
  if (phone === undefined) {
    return <p className="manage-note">The clinic’s phone number is on its website.</p>;
  }
  const href = telephoneHref(phone);
  if (href === null) return <p className="manage-note">Clinic phone: {phone}</p>;
  return (
    <a className="outline-button booking-link-button manage-call" href={href}>
      <Icon name="phone" />
      Call {phone}
    </a>
  );
}

/** The appointment's time, visit and clinician, on the clinic's clock when the session names it. */
export function AppointmentTicket({
  startAt,
  endAt,
  timeZone,
  visit,
  clinician,
  address,
}: {
  readonly startAt: string;
  readonly endAt: string;
  readonly timeZone: string | undefined;
  readonly visit: string;
  readonly clinician: string;
  readonly address: string;
}) {
  return (
    <>
      <section className="booking-confirmation-ticket manage-ticket" aria-label="Appointment">
        <div>
          <span>When</span>
          <strong>{formatDateTimeRange(startAt, endAt, timeZone)}</strong>
        </div>
        <div>
          <span>Visit</span>
          <strong>{visit}</strong>
        </div>
        <div>
          <span>Clinician</span>
          <strong>{clinician}</strong>
        </div>
        {address.trim() === "" ? null : (
          <small>
            <Icon name="location" />
            {address}
          </small>
        )}
      </section>
      <p className="manage-note">
        {timeZone === undefined
          ? "Times are shown in your device’s local time."
          : "Times are shown in the clinic’s local time."}
      </p>
    </>
  );
}

/**
 * What the link may still do, in one or two sentences with the clinic's phone where the answer is
 * "call" (lead decisions L3 and L4). A server from before PA-9 sends no rules: its link makes one
 * change and the time is changed by phone.
 */
function ChangeRules({ session }: { readonly session: PublicAppointmentSession }) {
  const selfService = session.selfService;
  if (selfService === undefined) {
    return (
      <p className="manage-note">
        Each link can make one change. Contact the clinic to reschedule or request another link.
      </p>
    );
  }
  const changeBy = formatDateTime(selfService.changeBy, session.timeZone);
  if (selfService.cancel === "past_cutoff") {
    return (
      <div className="manage-rules">
        <p>
          {sentence(`Online changes for this appointment closed on ${changeBy}`)} To cancel or
          change the time, call the clinic.
        </p>
        <CallTheClinic phone={session.clinicPhone} />
      </div>
    );
  }
  const until =
    Date.parse(selfService.changeBy) >= Date.parse(session.startAt)
      ? "until your appointment starts"
      : `until ${changeBy}`;
  if (selfService.reschedule === "available") {
    return (
      <div className="manage-rules">
        <p>{sentence(`You can change the time or cancel online ${until}`)}</p>
      </div>
    );
  }
  return (
    <div className="manage-rules">
      <p>{sentence(`You can cancel online ${until}`)}</p>
      <p>
        {selfService.reschedule === "not_eligible"
          ? "This appointment can’t be moved online. To change the time, call the clinic."
          : "To change the time, call the clinic."}
      </p>
      <CallTheClinic phone={session.clinicPhone} />
    </div>
  );
}

/**
 * A booking's manage link (PA-9): the appointment on the clinic's clock, Confirm while booked
 * (the link keeps working), Cancel with one of five reasons and a confirmation, and Change time
 * with the booking flow's time picker and a confirmation. What the clinic allows comes from the
 * session's selfService; a step the rules no longer allow falls back to the details, which say
 * why. Confirmation steps start on the safe choice.
 */
export function ManageAppointment({
  session,
  busy,
  statusMessage,
  error,
  actions,
}: {
  readonly session: PublicAppointmentSession;
  readonly busy: boolean;
  /** What the last request did, such as "Your appointment is confirmed." */
  readonly statusMessage: string | null;
  /** Why the last request was refused, shown under the step's heading. */
  readonly error: string | null;
  readonly actions: ManageActions;
}) {
  const [step, setStep] = useState<Step>("details");
  const [reason, setReason] = useState<PatientCancellationReason | null>(null);
  const [reasonMissing, setReasonMissing] = useState(false);
  const [times, setTimes] = useState<TimesDraft>(() => initialTimesDraft(session));
  const [timesReload, setTimesReload] = useState(0);
  // Stable, so the calendar's choice of a first day does not run on every render.
  const updateTimes = useCallback(
    (next: Partial<TimesDraft>) => setTimes((current) => ({ ...current, ...next })),
    [],
  );
  const stepHeading = useRef<HTMLHeadingElement>(null);
  const safeChoice = useRef<HTMLButtonElement>(null);
  const moved = useRef(false);

  const selfService = session.selfService;
  const canCancel =
    selfService === undefined
      ? session.status === "booked" || session.status === "confirmed"
      : selfService.cancel === "available";
  const canReschedule = selfService?.reschedule === "available";
  const shown: Step =
    step === "details"
      ? step
      : step === "cancel-reason" || step === "cancel-confirm"
        ? canCancel
          ? step
          : "details"
        : canReschedule
          ? step
          : "details";

  // A new step takes focus to its heading; a confirmation, to its safe choice. Not on first open:
  // the page heading has it then.
  useEffect(() => {
    if (!moved.current) return;
    if (shown === "cancel-confirm" || shown === "reschedule-confirm") safeChoice.current?.focus();
    else stepHeading.current?.focus();
  }, [shown]);

  const go = (next: Step) => {
    moved.current = true;
    actions.clearError();
    setReasonMissing(false);
    setStep(next);
  };
  const after = (outcome: ManageOutcome) => {
    if (outcome === "stay") return;
    moved.current = true;
    if (outcome === "times") {
      setTimes((current) => ({ ...current, slot: null, physician: null }));
      setTimesReload((current) => current + 1);
    }
    setStep(outcome);
  };

  const when = formatDateTimeRange(session.startAt, session.endAt, session.timeZone);
  // Under the heading the focus moves to, so it is seen first, not after a long list of times.
  const refusal =
    error === null ? null : (
      <p className="manage-error" role="alert">
        {error}
      </p>
    );

  if (shown === "cancel-reason") {
    return (
      <section className="manage-panel" aria-labelledby="manage-step-heading">
        <h2 id="manage-step-heading" ref={stepHeading} tabIndex={-1}>
          Cancel your appointment
        </h2>
        {refusal}
        <p className="manage-note">{when}</p>
        <fieldset className="booking-fieldset manage-reasons">
          <legend>
            Why are you cancelling?
            <RequiredMarker />
          </legend>
          <div className="booking-choice-grid">
            {PatientCancellationReasonSchema.options.map((value) => (
              <label key={value} className={reason === value ? "selected" : ""}>
                <input
                  type="radio"
                  name="cancellation-reason"
                  required
                  checked={reason === value}
                  onChange={() => {
                    setReason(value);
                    setReasonMissing(false);
                  }}
                />
                <span>
                  <strong>{cancellationReasonLabels[value]}</strong>
                </span>
                <i>
                  <Icon name="check" />
                </i>
              </label>
            ))}
          </div>
        </fieldset>
        <p className="manage-note">Only the clinic sees your answer.</p>
        {reasonMissing ? (
          <p className="manage-error" role="alert">
            Choose a reason to continue.
          </p>
        ) : null}
        <div className="manage-actions">
          <button
            type="button"
            className="continue-button"
            onClick={() => (reason === null ? setReasonMissing(true) : go("cancel-confirm"))}
          >
            Continue
          </button>
          <button type="button" className="outline-button" onClick={() => go("details")}>
            Keep appointment
          </button>
        </div>
      </section>
    );
  }

  if (shown === "cancel-confirm") {
    // A server from before PA-9 takes no reason.
    const sentReason = selfService === undefined ? undefined : (reason ?? undefined);
    return (
      <section className="manage-panel" aria-labelledby="manage-step-heading">
        <h2 id="manage-step-heading" ref={stepHeading} tabIndex={-1}>
          Cancel this appointment?
        </h2>
        {refusal}
        <p>{when}</p>
        {sentReason === undefined ? null : (
          <p className="manage-note">Reason: {cancellationReasonLabels[sentReason]}</p>
        )}
        <p className="manage-note">
          Cancelling uses up this link. To see the clinic another time, book a new appointment.
        </p>
        <div className="manage-actions">
          <button
            ref={safeChoice}
            type="button"
            className="outline-button"
            disabled={busy}
            onClick={() => go("details")}
          >
            Keep appointment
          </button>
          <button
            type="button"
            className="continue-button danger"
            disabled={busy}
            onClick={() => void actions.cancel(sentReason).then(after)}
          >
            {busy ? "Cancelling…" : "Yes, cancel appointment"}
          </button>
        </div>
      </section>
    );
  }

  if (shown === "times") {
    return (
      <section className="manage-panel" aria-labelledby="manage-step-heading">
        <h2 id="manage-step-heading" ref={stepHeading} tabIndex={-1}>
          Choose a new time
        </h2>
        {refusal}
        <p className="manage-note">Booked for {when}</p>
        <RescheduleTimes
          session={session}
          draft={times}
          update={updateTimes}
          loadTimes={actions.loadTimes}
          reload={timesReload}
        />
        <div className="manage-actions">
          <button
            type="button"
            className="continue-button"
            disabled={times.slot === null}
            onClick={() => go("reschedule-confirm")}
          >
            Continue
          </button>
          <button type="button" className="outline-button" onClick={() => go("details")}>
            Keep current time
          </button>
        </div>
      </section>
    );
  }

  if (shown === "reschedule-confirm" && times.slot !== null) {
    const slot = times.slot;
    return (
      <section className="manage-panel" aria-labelledby="manage-step-heading">
        <h2 id="manage-step-heading" ref={stepHeading} tabIndex={-1}>
          Change your appointment time?
        </h2>
        {refusal}
        <dl className="manage-change">
          <div>
            <dt>New time</dt>
            <dd>{formatDateTimeRange(slot.startAt, slot.endAt, session.timeZone)}</dd>
          </div>
          {times.physician === null ? null : (
            <div>
              <dt>Clinician</dt>
              <dd>{times.physician}</dd>
            </div>
          )}
          <div>
            <dt>Current time</dt>
            <dd>{when}</dd>
          </div>
        </dl>
        <p className="manage-note">
          Your current time is released and this link stops working. The next page gives you a new
          link.
        </p>
        <p className="manage-note">
          If this page doesn’t update after you confirm, call the clinic to check your appointment.
        </p>
        <div className="manage-actions">
          <button
            ref={safeChoice}
            type="button"
            className="outline-button"
            disabled={busy}
            onClick={() => go("details")}
          >
            Keep current time
          </button>
          <button
            type="button"
            className="continue-button"
            disabled={busy}
            onClick={() => void actions.reschedule(slot, times.physician).then(after)}
          >
            {busy ? "Changing…" : "Yes, change the time"}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="manage-panel" aria-labelledby="manage-step-heading">
      <h2 id="manage-step-heading" ref={stepHeading} tabIndex={-1}>
        Your appointment
      </h2>
      {refusal}
      <AppointmentTicket
        startAt={session.startAt}
        endAt={session.endAt}
        timeZone={session.timeZone}
        visit={session.appointmentTypeName}
        clinician={session.providerDisplayName}
        address={session.clinicAddress}
      />
      <p className="manage-status">
        Status: <strong>{statusLabel(session.status)}</strong>
      </p>
      {statusMessage === null ? null : (
        <p className="manage-saved" role="status">
          {statusMessage}
        </p>
      )}
      <ChangeRules session={session} />
      <div className="manage-actions">
        {session.status === "booked" ? (
          <button
            type="button"
            className="continue-button"
            disabled={busy}
            onClick={() => void actions.confirm().then(after)}
          >
            {busy ? "Saving…" : "Confirm appointment"}
          </button>
        ) : null}
        {canReschedule ? (
          <button
            type="button"
            className="outline-button"
            disabled={busy}
            onClick={() => go("times")}
          >
            Change time
          </button>
        ) : null}
        {canCancel ? (
          <button
            type="button"
            className="outline-button danger"
            disabled={busy}
            onClick={() => go(selfService === undefined ? "cancel-confirm" : "cancel-reason")}
          >
            Cancel appointment
          </button>
        ) : null}
      </div>
      <AddToCalendar
        appointment={{
          bookingReference: session.bookingReference,
          sequence: session.version,
          startAt: session.startAt,
          endAt: session.endAt,
          clinicName: session.clinicName,
          clinicAddress: session.clinicAddress,
        }}
      />
    </section>
  );
}
